import React, { useEffect } from 'react';
import { Plus } from 'lucide-react';
import { useChatContext } from '../providers/ChatProvider';
import { usePreviewContext } from '../providers/PreviewProvider';

export default function ChatInput({ onSend }) {
  const {
    input, setInput,
    isStreaming, setIsStreaming,
    streamingText, setStreamingText,
    maxInputChars,
    textareaRef,
    credits,
    theme,
    reply,
    setReply,
    handleClearChat,
    abortRef,
  } = useChatContext();

  const {
    handlePhotoUpload,
  } = usePreviewContext();

  // Auto-grow textarea height
  useEffect(() => {
    const tx = textareaRef.current;
    if (tx) {
      tx.style.height = 'auto';
      const newHeight = tx.scrollHeight;
      tx.style.height = `${Math.min(200, newHeight)}px`;
      tx.style.overflowY = newHeight > 200 ? 'auto' : 'hidden';
    }
  }, [input, textareaRef]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend(input);
    } else if (e.key === 'Enter' && e.shiftKey) {
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      const value = e.target.value;

      // Find the start of the current line
      const lastNewlineIdx = value.lastIndexOf('\n', selectionStart - 1);
      const lineStartIdx = lastNewlineIdx === -1 ? 0 : lastNewlineIdx + 1;

      // Extract the current line text up to the cursor
      const currentLineText = value.substring(lineStartIdx, selectionStart);

      // Check for numbered list
      const numMatch = currentLineText.match(/^(\d+)\.\s+/);
      if (numMatch) {
        const emptyMatch = currentLineText.match(/^(\d+)\.\s*$/);
        if (emptyMatch) {
          // Clear empty list prefix and add a simple newline
          const newValue = value.substring(0, lineStartIdx) + '\n' + value.substring(selectionEnd);
          setInput(newValue);
          setTimeout(() => {
            const tx = textareaRef.current || e.target;
            if (tx) {
              tx.selectionStart = tx.selectionEnd = lineStartIdx + 1;
            }
          }, 0);
        } else {
          const currentNum = parseInt(numMatch[1], 10);
          const nextNum = currentNum + 1;
          const prefix = `\n${nextNum}. `;
          const newValue = value.substring(0, selectionStart) + prefix + value.substring(selectionEnd);
          setInput(newValue);
          setTimeout(() => {
            const tx = textareaRef.current || e.target;
            if (tx) {
              tx.selectionStart = tx.selectionEnd = selectionStart + prefix.length;
            }
          }, 0);
        }
        e.preventDefault();
        return;
      }

      // Check for bullet list
      const bulletMatch = currentLineText.match(/^([*\-•])\s+/);
      if (bulletMatch) {
        const bullet = bulletMatch[1];
        const emptyMatch = currentLineText.match(/^([*\-•])\s*$/);
        if (emptyMatch) {
          // Clear empty bullet prefix and add a simple newline
          const newValue = value.substring(0, lineStartIdx) + '\n' + value.substring(selectionEnd);
          setInput(newValue);
          setTimeout(() => {
            const tx = textareaRef.current || e.target;
            if (tx) {
              tx.selectionStart = tx.selectionEnd = lineStartIdx + 1;
            }
          }, 0);
        } else {
          const prefix = `\n${bullet} `;
          const newValue = value.substring(0, selectionStart) + prefix + value.substring(selectionEnd);
          setInput(newValue);
          setTimeout(() => {
            const tx = textareaRef.current || e.target;
            if (tx) {
              tx.selectionStart = tx.selectionEnd = selectionStart + prefix.length;
            }
          }, 0);
        }
        e.preventDefault();
        return;
      }
    }
  };

  const handleStop = () => {
    if (abortRef.current) abortRef.current.abort();
    setIsStreaming(false);
    if (streamingText.trim()) {
      setReply((prev) => [
        ...prev,
        { role: 'assistant', content: streamingText.trim() },
      ]);
    }
    setStreamingText('');
  };

  return (
    <div className="px-4 pb-20 pt-3 md:pb-4">
      {/* Credits low banner inside chat */}
      {credits !== null && credits <= 5 && credits > 0 && (
        <div className="flex items-center justify-between gap-2 mb-1.5 bg-amber-900/15 border border-amber-500/20 rounded-lg px-2.5 py-1.5">
          <p className="text-amber-300/90 text-[10px] font-medium">⚠️ {credits} credit{credits === 1 ? '' : 's'} left</p>
        </div>
      )}
      {credits === 0 && (
        <div className="flex items-center justify-between gap-2 mb-1.5 bg-red-900/15 border border-red-500/20 rounded-lg px-2.5 py-1.5">
          <p className="text-red-300/90 text-[10px] font-medium">🚫 No credits — chat paused</p>
        </div>
      )}


      {/* Photo attach via chat + */}
      <input type="file" id="chat-photo-input-chat" onChange={handlePhotoUpload} accept="image/png, image/jpeg, image/webp" className="hidden" />
      <div className={`flex items-center gap-2 rounded-[28px] pl-5 pr-4 py-2.5 border outline-none focus-within:outline-none ${input.length > maxInputChars * 0.9
        ? 'border-red-500 bg-red-500/5'
        : theme === 'dark'
          ? 'bg-zinc-900/50 border-zinc-800'
          : 'bg-slate-50 border-slate-200'
        }`} style={{ minHeight: 56 }}>
        {/* + attachment button */}
        <button
          type="button"
          onClick={() => document.getElementById('chat-photo-input-chat')?.click()}
          title="Attach photo to resume"
          className={`w-7 h-7 flex items-center justify-center rounded-full active:scale-95 transition-all shrink-0 ${theme === 'dark'
            ? 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-200'
            }`}
        >
          <Plus size={18} strokeWidth={2.2} />
        </button>
        <textarea
          ref={textareaRef}
          value={input}
          onChange={(e) => {
            if (e.target.value.length <= maxInputChars) setInput(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          placeholder={
            isStreaming
              ? 'Thinking...'
              : credits === 0
                ? 'Chat paused...'
                : 'Message AI...'
          }
          disabled={isStreaming || credits === 0}
          rows={1}
          maxLength={maxInputChars}
          className={`flex-1 bg-transparent text-[13.5px] border-0 focus:border-0 focus:outline-none focus:ring-0 shadow-none focus:shadow-none py-1.5 px-2 resize-none leading-relaxed disabled:opacity-60 transition-opacity duration-200 font-sans ${theme === 'dark' ? 'text-zinc-100 placeholder-zinc-500' : 'text-slate-800 placeholder-slate-400'}`}
        />
        {isStreaming ? (
          <button
            onClick={handleStop}
            title="Stop generating"
            className={`w-7 h-7 flex items-center justify-center rounded-full active:scale-[0.93] transition-all duration-200 shrink-0 ${theme === 'dark'
              ? 'bg-white text-black hover:bg-zinc-200'
              : 'bg-black text-white hover:bg-zinc-800'
              }`}
          >
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
          </button>
        ) : (
          <button
            onClick={() => onSend(input)}
            disabled={!input.trim() || credits === 0 || input.length > maxInputChars}
            className={`w-7 h-7 flex items-center justify-center rounded-full active:scale-[0.93] transition-all duration-200 shrink-0 ${theme === 'dark'
              ? 'bg-white text-black hover:bg-zinc-200 disabled:bg-zinc-850 disabled:text-zinc-650'
              : 'bg-black text-white hover:bg-slate-800 disabled:bg-slate-200 disabled:text-slate-400'
              }`}
          >
            <svg className={`w-3.5 h-3.5 ${theme === 'dark' ? 'text-black' : 'text-white'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18" />
            </svg>
          </button>
        )}
      </div>
      <div className="flex items-center justify-between mt-1.5 px-1">
        <p className={`text-[10px] hidden sm:block ${theme === 'dark' ? 'text-zinc-400' : 'text-slate-600'}`}>
          <kbd className="px-1 py-0.5 rounded font-mono text-[9px] border" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>Enter</kbd> send ·{' '}
          <kbd className="px-1 py-0.5 rounded font-mono text-[9px] border" style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>Shift+Enter</kbd> new line
        </p>
        <span className={`text-[10px] tabular-nums transition-colors ml-auto sm:ml-0 ${input.length > maxInputChars * 0.9 ? 'text-red-400 font-semibold' : input.length > maxInputChars * 0.7 ? 'text-amber-500' : (theme === 'dark' ? 'text-zinc-450' : 'text-slate-600')
          }`}>
          {input.length}/{maxInputChars}
        </span>
      </div>
    </div>
  );
}
